import { ISalesforceOpportunityDTO, ISalesforceOrderDAO } from '../../../../../api/salesforce-order.dao';
import { FormVmValueType } from '../../../../../infrastructure/form-viewmodel-controller';
import { ILocalStorageResponse, ILocalStorageService } from '../../../../../infrastructure/local-storage-service';
import { IOrderFormSelectOption } from './order-form-fields.types';

export const SF_ACCOUNT_SEARCH_CACHE_MINUTES = 15;

const accountSearchCacheKey = (prefix: string, ownerId: number, fieldKey: string): string =>
    `${prefix}-${fieldKey}-${ownerId}`;

export const salesforceAccountSearchCacheKey = (eventId: number, fieldKey: string): string =>
    accountSearchCacheKey('sf-account-search', eventId, fieldKey);

export const orderTemplateAccountSearchCacheKey = (accountId: number, fieldKey: string): string =>
    accountSearchCacheKey('order-template-account-search', accountId, fieldKey);

export const salesforceOpportunitySearchCacheKey = (eventId: number): string =>
    `sf-opportunity-search-${eventId}`;

export const orderTemplateOpportunitySearchCacheKey = (accountId: number): string =>
    `order-template-opportunity-search-${accountId}`;

export const toOpportunitySelectOptions = (
    items: ISalesforceOpportunityDTO[] | null | undefined,
): IOrderFormSelectOption[] =>
    (items ?? []).map(item => ({
        value: item.id,
        label: item.name,
        columns: [
            { key: 'lookupTitle', value: item.name },
            { key: 'accountName', value: item.accountName ?? '' },
        ],
    }));

interface IOpportunitySearchCacheEntry {
    term: string;
    opportunities: ISalesforceOpportunityDTO[];
}

export const getCachedOpportunitySearch = (
    localStorageService: ILocalStorageService,
    cacheKey: string,
): { term: string; options: IOrderFormSelectOption[] } | null => {
    const cached = localStorageService.getLocalStorageValue<ILocalStorageResponse>(cacheKey);
    if (cached && cached.value && localStorageService.checkTimeRange(cached.creationDate, cacheKey, SF_ACCOUNT_SEARCH_CACHE_MINUTES)) {
        const entry = cached.value as IOpportunitySearchCacheEntry;
        return { term: entry.term, options: toOpportunitySelectOptions(entry.opportunities) };
    }
    return null;
};

export const searchOpportunitiesWithCache = async (
    localStorageService: ILocalStorageService,
    salesforceOrderDAO: ISalesforceOrderDAO,
    cacheKey: string,
    accountId: number,
    term: string,
    isStillCurrent: () => boolean,
): Promise<IOrderFormSelectOption[]> => {
    const cached = getCachedOpportunitySearch(localStorageService, cacheKey);
    if (cached && cached.term === term) {
        return cached.options;
    }
    const opportunities = await salesforceOrderDAO.searchOpportunities({ accountId: accountId, searchTerm: term });
    const options = toOpportunitySelectOptions(opportunities);
    if (!isStillCurrent()) {
        return options;
    }
    localStorageService.setLocalStorageValue({ name: cacheKey, value: { term: term, opportunities: opportunities }, creationDate: Date.now() });
    return options;
};

export type LookupFieldKey = 'corporateRelated' | 'billToLegalEntity' | 'channelAgencyReferral' | 'opportunity';

export interface ILookupFieldAccessor {
    get: () => IOrderFormSelectOption | null;
    set: (option: IOrderFormSelectOption | null) => void;
}

/**
 * Shared dispatch for the lookup fields (Corporate Related, Bill To: Legal Entity,
 * Channel/Agency Referral, Opportunity) used by both the Create/Edit Order form and the Order
 * Template form. Centralizing this here keeps "unknown fieldKey" behavior (returns null, no-op
 * set) identical across both viewmodels instead of each one defining its own divergent fallback.
 */
export const createLookupFieldDispatch = (accessors: Record<LookupFieldKey, ILookupFieldAccessor>) => ({
    getInitialOption: (fieldKey: string): IOrderFormSelectOption | null =>
        accessors[fieldKey as LookupFieldKey]?.get() ?? null,
    setOption: (fieldKey: string, option: IOrderFormSelectOption | null): void => {
        accessors[fieldKey as LookupFieldKey]?.set(option);
    },
});

export const stringValue = (value: FormVmValueType): string =>
    value == null ? '' : String(value);

export const booleanValue = (value: FormVmValueType): boolean =>
    value === true || value === 'true' || value === 'Yes';

export const toIdNameOptions = <T extends { id: string; name: string }>(
    items: T[] | null | undefined,
): IOrderFormSelectOption[] =>
    (items ?? []).map(item => ({ value: item.id, label: item.name }));
